/**
 * Les sons du scan (SCAN-QR § 3.1, 5) : vert, un bip aigu ; orange, un double bip ; rouge, un
 * son grave ; gris, un bip court et neutre. Chaque scan sonne sans jamais bloquer le suivant.
 *
 * Un `AudioContext` ne joue sur iPhone que s'il a été ouvert (ou repris) pendant un geste :
 * `ouvrirLeSon()` s'appelle dans l'appui « Scanner » (et « Tester mon téléphone »). Le bouton
 * silencieux de l'iPhone coupe ces sons (HYPOTHÈSE À VÉRIFIER sur appareil) : le flash du viseur
 * et la vibration, quand elle existe, disent la même chose. PROPOSITION TECHNIQUE.
 */
import type { Ton } from './retours';

export interface Note {
  /** Hz. */
  frequence: number;
  /** ms après le début du son. */
  debut: number;
  /** ms. */
  duree: number;
}

/** Le motif de chaque retour (pur, éprouvé). */
export function motifDuSon(ton: Ton): Note[] {
  switch (ton) {
    case 'vert':
      return [{ frequence: 1320, debut: 0, duree: 110 }];
    case 'orange':
      return [
        { frequence: 880, debut: 0, duree: 90 },
        { frequence: 880, debut: 150, duree: 90 },
      ];
    case 'rouge':
      return [{ frequence: 220, debut: 0, duree: 320 }];
    case 'gris':
    default:
      return [{ frequence: 660, debut: 0, duree: 70 }];
  }
}

/** La vibration de chaque retour (ms : vibre, pause, vibre…), là où le navigateur la permet (Android). */
export function motifDeVibration(ton: Ton): number[] {
  switch (ton) {
    case 'vert':
      return [60];
    case 'orange':
      return [60, 80, 60];
    case 'rouge':
      return [250];
    case 'gris':
    default:
      return [30];
  }
}

type FabriqueAudio = new () => AudioContext;

let contexte: AudioContext | null = null;

function fabrique(): FabriqueAudio | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: FabriqueAudio; webkitAudioContext?: FabriqueAudio };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Ouvre (ou reprend) le contexte audio. À appeler **dans un geste** de l'utilisateur. Rend
 * vrai si le son est prêt à jouer.
 */
export function ouvrirLeSon(): boolean {
  try {
    if (!contexte) {
      const F = fabrique();
      if (!F) return false;
      contexte = new F();
    }
    if (contexte.state === 'suspended') void contexte.resume();
    return contexte.state !== 'closed';
  } catch {
    return false;
  }
}

/** Le son est-il ouvert et en marche ? (« Tester mon téléphone ») */
export function sonPret(): boolean {
  return !!contexte && contexte.state === 'running';
}

/** Joue le retour d'un scan ; ne lève jamais (un son qui manque ne bloque rien). */
export function jouer(ton: Ton): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(motifDeVibration(ton));
  } catch {
    /* pas de vibration */
  }
  const c = contexte;
  if (!c || c.state === 'closed') return;
  try {
    if (c.state === 'suspended') void c.resume();
    const t0 = c.currentTime + 0.01;
    for (const n of motifDuSon(ton)) {
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = ton === 'rouge' ? 'square' : 'sine';
      osc.frequency.value = n.frequence;
      const debut = t0 + n.debut / 1000;
      const fin = debut + n.duree / 1000;
      gain.gain.setValueAtTime(0.0001, debut);
      gain.gain.exponentialRampToValueAtTime(0.3, debut + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, fin);
      osc.connect(gain).connect(c.destination);
      osc.start(debut);
      osc.stop(fin + 0.02);
    }
  } catch {
    /* le son est un plus, jamais une condition */
  }
}
